/**
 * Sales API Client
 * Handles all API calls related to sales management
 */

import client from './client';

/**
 * Create a new sale
 * @param {Object} saleData - Sale information
 * @returns {Promise} Sale data with receipt
 */
export const createSale = async (saleData) => {
  const response = await client.post('/sales', saleData);
  return response.data;
};

/**
 * Get all sales with optional filters
 * @param {Object} filters - Filter options
 * @returns {Promise} Array of sales
 */
export const getAllSales = async (filters = {}) => {
  const params = new URLSearchParams();

  if (filters.start_date || filters.startDate) params.append('start_date', filters.start_date || filters.startDate);
  if (filters.end_date || filters.endDate) params.append('end_date', filters.end_date || filters.endDate);
  if (filters.customer_id) params.append('customer_id', filters.customer_id);
  if (filters.payment_method || filters.paymentMethod) params.append('payment_method', filters.payment_method || filters.paymentMethod);
  if (filters.payment_status || filters.paymentStatus) params.append('payment_status', filters.payment_status || filters.paymentStatus);
  if (filters.status) params.append('status', filters.status);
  if (filters.branch_id || filters.branchId) params.append('branch_id', filters.branch_id || filters.branchId);
  if (filters.search) params.append('search', filters.search);
  if (filters.limit) params.append('limit', filters.limit);
  if (filters.offset) params.append('offset', filters.offset);
  if (filters.page) params.append('page', filters.page);

  const response = await client.get(`/sales?${params.toString()}`);
  return response.data;
};

/**
 * Get sale by ID
 * @param {number} saleId - Sale ID
 * @returns {Promise} Sale details with items and comments
 */
export const getSaleById = async (saleId) => {
  const response = await client.get(`/sales/${saleId}`);
  return response.data;
};

/**
 * Add comment to a sale
 * @param {number} saleId - Sale ID
 * @param {Object} commentData - Comment data
 * @returns {Promise} Created comment
 */
export const addComment = async (saleId, commentData) => {
  const response = await client.post(`/sales/${saleId}/comments`, commentData);
  return response.data;
};

/**
 * Get sales analytics
 * @param {Object} filters - Date range filters
 * @returns {Promise} Analytics data
 */
export const getSalesAnalytics = async (filters = {}) => {
  const params = new URLSearchParams();

  if (filters.start_date) params.append('start_date', filters.start_date);
  if (filters.end_date) params.append('end_date', filters.end_date);

  const response = await client.get(`/sales/analytics?${params.toString()}`);
  return response.data;
};

/**
 * Update a sale (Admin only)
 * @param {number} saleId - Sale ID
 * @param {Object} updates - Updated data
 * @returns {Promise} Updated sale
 */
export const updateSale = async (saleId, updates) => {
  const response = await client.put(`/sales/${saleId}`, updates);
  return response.data;
};

/**
 * Delete a sale (Super Admin only)
 * @param {number} saleId - Sale ID
 * @returns {Promise} Deletion confirmation
 */
export const deleteSale = async (saleId) => {
  const response = await client.delete(`/sales/${saleId}`);
  return response.data;
};

/**
 * Search products for sale form
 * @param {string} searchTerm - Search query
 * @returns {Promise} Array of products
 */
export const searchProducts = async (searchTerm) => {
  const response = await client.get(`/products?search=${searchTerm}&limit=20`);
  return response.data;
};

/**
 * Search customers for sale form
 * @param {string} searchTerm - Search query
 * @returns {Promise} Array of customers
 */
export const searchCustomers = async (searchTerm) => {
  const response = await client.get(`/customers?search=${searchTerm}&limit=20`);
  return response.data;
};

/**
 * Search referrers for sale form
 * @param {string} searchTerm - Search query
 * @returns {Promise} Array of referrers
 */
export const searchReferrers = async (searchTerm) => {
  const response = await client.get(`/referrers?search=${searchTerm}&limit=20`);
  return response.data;
};

// Bank Channel & Confirmation Methods

/**
 * Get sales awaiting confirmation
 * @param {Object} params - Query params (status, page, limit)
 * @returns {Promise} List of sales
 */
export const getAwaitingConfirmation = async (params = {}) => {
  const query = new URLSearchParams(params).toString();
  const response = await client.get(`/sales/awaiting-confirmation?${query}`);
  return response.data;
};

/**
 * Confirm a sale payment (Admin)
 * @param {number} saleId - Sale ID
 * @param {number} actualCharge - Optional actual charge
 * @returns {Promise} Confirmation result
 */
export const confirmPayment = async (saleId, actualCharge) => {
  const data = actualCharge !== undefined ? { actual_channel_charge: actualCharge } : {};
  const response = await client.post(`/sales/${saleId}/confirm`, data);
  return response.data;
};

/**
 * Set bank channel for pending sale (Staff)
 * @param {number} saleId - Sale ID
 * @param {number} bankChannelId - Bank channel ID
 * @returns {Promise} Result
 */
export const setBankChannel = async (saleId, bankChannelId) => {
  const response = await client.put(`/sales/${saleId}/bank-channel`, { bank_channel_id: bankChannelId });
  return response.data;
};

/**
 * Add query/comment to pending sale
 * @param {number} saleId - Sale ID
 * @param {string} comment - Query text
 * @returns {Promise} Result
 */
export const queryPendingSale = async (saleId, comment) => {
  const response = await client.post(`/sales/${saleId}/query`, { comment_text: comment });
  return response.data;
};

/**
 * Get pending sales for current user
 * @returns {Promise} List of sales
 */
export const getMyPendingSales = async () => {
  const response = await client.get(`/sales/my-pending`);
  return response.data;
};

/**
 * Reverse/Cancel a sale (Admin)
 * @param {number} saleId - Sale ID
 * @returns {Promise} Result
 */
export const reverseSale = async (saleId) => {
  const response = await client.post(`/sales/${saleId}/reverse`);
  return response.data;
};

/**
 * Unreverse/Reactivate a cancelled sale (Admin)
 * @param {number} saleId - Sale ID
 * @returns {Promise} Result
 */
export const unreverseSale = async (saleId) => {
  const response = await client.post(`/sales/${saleId}/unreverse`);
  return response.data;
};

// Export aliases for convenience
export const getSales = getAllSales;
export const addSaleComment = addComment;

